import { AssetDevice } from 'app/pages/ValidationsPage/type';
import axios from 'axios';
import dayjs from 'dayjs';

/** =========================
 * Payload Types
 * ========================= */

/** Create / Update Location Inspection */
interface payloadCreateLocationInspection {
  outside: File;
  internal: File;
  clientId: number;
  lat?: number;
  long?: number;
  date: string;
  roomId: string;
  approver: string;
  documentControl: string;
  id?: number;
  deletePhotoCustomAttr?: number[];
  deletePhotoRoomLevelIssue?: number[];
}
/** Room Level Issue (Create) */
export interface CreateRoomLevelIssuePayload {
  description?: string;
  submitId?: string;
  file?: File;
  uploadedId?: number;
}
/**
 *
 * @param obj
 * @returns payload
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const appendDynamicOptionForPayload = (obj: Record<string, any>) => {
  const payload: Record<string, string>[] = [];
  for (const key in obj) {
    if (!key.startsWith('option') || typeof obj[key] === 'object' || obj[key] === 'required:false')
      continue;

    let optionName = ' ';
    if (key.startsWith('option') && !key.endsWith('_name') && obj[key] !== 'OTHER' && obj[key]) {
      optionName = key.slice(7);
      payload.push({ [optionName]: obj[key] });
    }
    if (key.endsWith('_name') && obj[key.slice(0, -5)] === 'OTHER' && obj[key]) {
      optionName = key.slice(7);
      optionName = optionName.slice(0, -5);
      payload.push({ [optionName]: obj[key] });
    }
  }
  return payload;
};
/**
 * single_
 * @param obj
 * @returns payload
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const appendDynamicSingleForPayload = (obj: Record<string, any>) => {
  const payload: Record<string, string>[] = [];
  for (const key in obj) {
    if (!key.startsWith('single') || typeof obj[key] === 'object' || obj[key] === 'required:false')
      continue;

    let optionName = ' ';
    if (key.startsWith('single') && !key.endsWith('_name') && obj[key] !== 'OTHER' && obj[key]) {
      optionName = key.slice(7);
      payload.push({ [optionName]: obj[key] });
    }
    if (key.endsWith('_name') && obj[key.slice(0, -5)] === 'OTHER' && obj[key]) {
      optionName = key.slice(7);
      optionName = optionName.slice(0, -5);
      payload.push({ [optionName]: obj[key] });
    }
  }

  return payload;
};

/**
 * START INSPECTION — ADVANCED REFACTORED API LAYER
 * Converted from React Native Blob style → Axios JSON/multipart auto-handled by backend
 */

/** Create or Update Location Inspection */
export const createLocationInspection = async (payload: payloadCreateLocationInspection) => {
  const form = new FormData();
  const options: Record<string, string> = {};
  const singleFields: Record<string, string> = {};
  // required files
  form.append('outside', payload.outside);
  form.append('internal', payload.internal);

  Object.entries(payload).forEach(([key, value]) => {
    if (key === 'outside' || key === 'internal') return;
    if (value === undefined || value === null) return;

    // OPTION
    if (key.startsWith('option_')) {
      let id = key.slice(7);
      if (id.endsWith('_name')) id = id.replace('_name', '');
      options[id] = value as string;
      return;
    }

    // SINGLE
    if (key.startsWith('single_')) {
      let id = key.slice(7);
      if (id.endsWith('_name')) id = id.replace('_name', '');

      // detect date
      const formatted = dayjs(value as string).isValid()
        ? dayjs(value as string).format('YYYY/MM/DD')
        : (value as string);

      singleFields[id] = formatted;
      return;
    }

    // NORMAL FIELD
    form.append(key, String(value));
  });
  form.append('options', JSON.stringify(options));
  form.append('singleFields', JSON.stringify(singleFields));
  const { data } = await axios.post('/inspections/locations', form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  return data;
};

/** Create Room Level Issue */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createRoomLevelIssue = async (payload: any, id: number) => {
  const form = new FormData();

  const options = appendDynamicOptionForPayload(payload);
  const singleFields = appendDynamicSingleForPayload(payload);

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (key.startsWith('option_')) return;
    if (key.startsWith('single_')) return;

    // convert validated "Yes"/"No" to boolean
    if (key === 'validated') {
      const boolVal = value === true || value === 'true' || value === 'Yes' || value === 'yes';
      form.append('validated', String(boolVal));
      return;
    }

    if (value instanceof File) {
      form.append('files', value);
      return;
    }

    if (key === 'files' && Array.isArray(value)) {
      value.forEach((f: File) => form.append('files', f));
      return;
    }

    if (key === 'deletePhotoIds' && Array.isArray(value)) {
      form.append('deletePhotoIds', JSON.stringify(value));
      return;
    }

    form.append(key, String(value));
  });

  form.append('options', JSON.stringify(options));
  form.append('singleFields', JSON.stringify(singleFields));

  const { data } = await axios.post(`/inspections/locations/${id}/room-issues`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  return data;
};

/** Update Room Level Issue */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateRoomLevelIssue = async (payload: any, id: number) => {
  if (!payload.id) throw new Error('Missing room issue id');
  const form = new FormData();

  const options = appendDynamicOptionForPayload(payload);
  const singleFields = appendDynamicSingleForPayload(payload);

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || key === 'url' || value === '') return;

    if (key.startsWith('option_')) return;
    if (key.startsWith('single_')) return;

    // convert validated "Yes"/"No" to boolean
    if (key === 'validated') {
      const boolVal = value === true || value === 'true' || value === 'Yes' || value === 'yes';
      form.append('validated', String(boolVal));
      return;
    }

    if (value instanceof File) {
      form.append('files', value);
      return;
    }

    if (key === 'files' && Array.isArray(value)) {
      value.forEach((f: File) => form.append('files', f));
      return;
    }

    if (key === 'deletePhotoIds' && Array.isArray(value)) {
      form.append('deletePhotoIds', JSON.stringify(value));
      return;
    }

    if (key === 'archivePhotoIds') {
      if (Array.isArray(value) && value.length > 0) {
        form.append('archivePhotoIds', JSON.stringify(value));
      }
      return;
    }

    if (key === 'photoTypes') {
      form.append('photoTypes', String(value));
      return;
    }

    if (key === 'criticalityKey' && value === '') {
      form.append('criticalityKey', '');
      return;
    }

    form.append(key, String(value));
  });

  form.append('options', JSON.stringify(options));
  form.append('singleFields', JSON.stringify(singleFields));

  const { data } = await axios.put(`/inspections/locations/${id}/room-issues/${payload.id}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return data;
};

/** Create Custom Attribute */
export const createCustomAttr = async (
  payload: { desc: string; field: string; file: File },
  id: number
) => {
  const form = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (value instanceof File) {
      form.append(key, value);
    } else {
      form.append(key, String(value));
    }
  });

  const { data } = await axios.post(`/inspections/locations/${id}/custom-attributes`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  return data;
};

/** Update Custom Attribute */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateCustomAttr = async (payload: any, id: number) => {
  if (!payload.id) throw new Error('Missing custom attribute id');
  const { data } = await axios.put(
    `/inspections/locations/${id}/custom-attributes/${payload.id}`,
    payload
  );
  return data;
};

/** Create Wall (storage) */
export const createWall = async (payload: { files: File[]; name: string }, id: number) => {
  const form = new FormData();
  payload.files.forEach((f) => form.append('files', f));
  const { data } = await axios.post(`/storages/inspections/${id}/${payload.name}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  return data;
};

/** Create Additional Structure */
export const createAdditionalStructure = async (id: number, type: string) => {
  const { data } = await axios.post(`/inspections/locations/${id}/additional-structures`, { type });
  return data;
};

/** Create Additional Structure Photos */
export const createAdditionalStructurePhoto = async (
  payload: { structureId: number; files: File[] },
  id: number
) => {
  const form = new FormData();
  payload.files.forEach((f) => form.append('files', f));
  const { data } = await axios.post(
    `/storages/inspections/${id}/additional-structures/${payload.structureId}`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' }
    }
  );
  return data;
};

/** Get Inspection Location by ID */
export const getInspectionLocationById = async (id: number) => {
  const { data } = await axios.get(`/inspections/locations/${id}`);
  return data;
};

/** Delete Wall Photos */
export const deleteWall = async ({
  submittedId,
  wall,
  ids
}: {
  submittedId: number;
  wall: string;
  ids: number[];
}) => {
  const { data } = await axios.put(`/storages/inspections/${submittedId}/${wall}/remove`, { ids });
  return data;
};

/** Delete Additional Structure Photos */
export const deleteAdditionalStructure = async ({
  submittedId,
  structureId,
  ids
}: {
  submittedId: number;
  structureId: number;
  ids: number[];
}) => {
  const { data } = await axios.put(
    `/storages/inspections/${submittedId}/additional-structures/${structureId}/remove`,
    { ids }
  );
  return data;
};

/** Upload File */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const uploadFile = async (file: any) => {
  const { data } = await axios.post(`/file/upload`, file);
  return data;
};

/** Create or Update Submitted Asset (Admin - FormData version) */
export const createSubmittedAssets = async ({
  payload,
  submittedId
}: {
  payload: {
    name: string;
    typeId: number;
    // criticality: number;
    criticalityKey: string;
    note?: string;
    otherNote?: string;
    modelId: number;
    altText: string;
    id?: number;
    photos: File[];
    entries: string[];
    model?: number;
    manufacturer?: string;
    manufacturerId?: number;
    conditionName?: string;
    assetCustomId?: string;
    assetCustomId2?: string;
    lastMaintenance?: string;
    maintenanceEnabled: boolean;
    assetDevices: AssetDevice[];
  };
  submittedId: number;
}) => {
  const form = new FormData();
  const options = appendDynamicOptionForPayload(payload);
  const singleFields = appendDynamicSingleForPayload(payload);
  // append regular fields
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (key.startsWith('option_')) return;
    if (key.startsWith('single_')) return;
    // handle files array
    if (key === 'photos' && Array.isArray(value)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (value as any).forEach((file: File) => form.append('files', file));
      return;
    }
    if (key === 'entries') {
      const normalized = Array.isArray(value)
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          value.map((e: any) => ({
            ...e,
            fieldId: Number(e.fieldId)
          }))
        : [];
      form.append('entries', JSON.stringify(normalized));
      return;
    }

    if (!(value instanceof File)) {
      form.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
      return;
    }

    // append single file
    form.append(key, value);
  });

  form.append('options', JSON.stringify(options));
  form.append('singleFields', JSON.stringify(singleFields));

  const { data } = await axios.post(
    `/inspections/locations/${submittedId}/submitted-assets`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' }
    }
  );

  return data;
};
